import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { faCheck, faHouseUser, faLock, faPlus } from "@fortawesome/free-solid-svg-icons";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import {
    Content,
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    FloatInput,
    ContentLoader,
    Button,
    Icon,
    Table,
    LoadingSpinner,
    Input,
    Switch,
    ButtonGroup,
    Modal,
    ModalTitle,
    Alert,
} from "@webtypen/react-components";
import { Page } from "../../components/Page";
import { ApiHelper, ConfirmHelper } from "../../helpers";

const defaultState = {
    name: "",
    short_name: "",
    number: "",
    addresses: [],
    persons: [],
};

export const CustomersEdit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [data, setData] = useState(null);
    const [personNewPassword, setPersonNewPassword] = useState(null);

    const onChange = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value,
        });
    };

    const onAddAddress = () => {
        const newData = { ...data };
        if (!newData.addresses || newData.addresses.length < 1) {
            newData.addresses = [];
        }

        newData.addresses.push({
            street: "",
            housenumber: "",
            zip: "",
            city: "",
        });
        setData(newData);
    };

    const onAddressChange = (e) => {
        const index = parseInt(e.target.getAttribute("data-index"));

        const newData = { ...data };
        newData.addresses[index][e.target.name] = e.target.value;
        setData(newData);
    };

    const onAddressRemove = (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));
        const newData = { ...data };
        newData.addresses.splice(index, 1);
        setData(newData);
    };

    const onAddPerson = () => {
        const newData = { ...data };
        if (!newData.persons || newData.persons.length < 1) {
            newData.persons = [];
        }

        newData.persons.push({
            identifier: "new",
            firstname: "",
            lastname: "",
            email: "",
            is_active: false,
            new_password: "",
        });
        setData(newData);
    };

    const onPersonChange = (e) => {
        const index = parseInt(e.target.getAttribute("data-index"));

        const newData = { ...data };
        newData.persons[index][e.target.name] = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setData(newData);
    };

    const onPersonActiveChange = (index, checked) => {
        const newData = { ...data };
        newData.persons[parseInt(index)].is_active = checked ? true : false;
        setData(newData);
    };

    const onPersonRemove = async (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));
        const newData = { ...data };
        newData.persons.splice(index, 1);
        setData(newData);
    };

    const openPersonNewPassword = (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));
        setPersonNewPassword(index);
    };

    const closePersonNewPassword = () => {
        setPersonNewPassword(null);
    };

    const submit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsSaving(true);
        ApiHelper.request("/api/customers/save", {
            ...data,
            _id: params.id,
        })
            .then((response) => {
                if (response.status === "success" && response.data && response.data._id) {
                    navigate("/customers");
                } else {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                    setIsSaving(false);
                }
            })
            .catch((e) => {
                console.error(e);

                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                setIsSaving(false);
            });
    };

    useEffect(() => {
        if (params.id === "new") {
            setData({ ...defaultState });
            setIsLoading(false);
        } else {
            setIsLoading(true);
            ApiHelper.request("/api/customers/data", {
                _id: params.id,
            })
                .then((response) => {
                    setData(response.status === "success" && response.data && response.data._id ? response.data : null);
                    setIsLoading(false);
                })
                .catch((e) => {
                    console.error(e);
                    setData(null);
                    setIsLoading(false);
                });
        }
    }, [params.id]);

    if (isLoading || !data) {
        return (
            <Page icon={faHouseUser} title="Kunden" back="/customers">
                <Content>
                    {isLoading ? <ContentLoader /> : <Alert danger>Fehler beim Laden des Datensatzes ...</Alert>}
                </Content>
            </Page>
        );
    }

    return (
        <Page icon={faHouseUser} title="Kunden" back="/customers">
            {personNewPassword !== null ? (
                <Modal
                    open={true}
                    contentAttributes={{
                        style: {
                            maxWidth: 550,
                            margin: "auto",
                            display: "flex",
                            maxHeight: "95vh",
                            flexDirection: "column",
                        },
                    }}
                >
                    <ModalTitle>
                        <h1>Neues Passwort setzen</h1>
                    </ModalTitle>
                    <Content>
                        <FloatInput
                            type="password"
                            placeholder="Neues Passwort"
                            name="new_password"
                            value={
                                data.persons[personNewPassword].new_password
                                    ? data.persons[personNewPassword].new_password
                                    : ""
                            }
                            onChange={onPersonChange}
                            disabled={isSaving}
                            data-index={personNewPassword}
                        />
                        <div className="mt-2 text-right">
                            <Button type="button" onClick={closePersonNewPassword}>
                                Okay
                            </Button>
                        </div>
                    </Content>
                </Modal>
            ) : null}

            <Content>
                <form onSubmit={submit}>
                    <Row>
                        <Col width={12}>
                            <Card className="mb-3">
                                <CardHeader>Hauptinformationen</CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col width={6} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="name"
                                                placeholder="Name"
                                                value={data.name ? data.name : ""}
                                                onChange={onChange}
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col width={3} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="short_name"
                                                placeholder="Kurzname"
                                                value={data.short_name ? data.short_name : ""}
                                                onChange={onChange}
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col width={3} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="number"
                                                placeholder="Kundennummer"
                                                value={data.number ? data.number : ""}
                                                onChange={onChange}
                                                disabled={isSaving}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col width={4}>
                            <Card className="mb-3">
                                <CardHeader>
                                    <Button
                                        type="button"
                                        className="float-right"
                                        disabled={isSaving}
                                        onClick={onAddAddress}
                                        xs
                                    >
                                        <Icon icon={faPlus} className="mr-1" />
                                        Hinzufügen
                                    </Button>
                                    Adressen
                                </CardHeader>
                                {!data.addresses || data.addresses.length < 1 ? (
                                    <CardBody>Es wurden noch keine Adressen hinzugefügt ...</CardBody>
                                ) : (
                                    data.addresses.map((addr, i) => (
                                        <CardBody
                                            key={i}
                                            style={parseInt(i) > 0 ? { borderTop: "1px solid #eee" } : null}
                                        >
                                            <Row>
                                                <Col width={8}>
                                                    <FloatInput
                                                        type="text"
                                                        name="street"
                                                        placeholder="Straße"
                                                        className="mb-2"
                                                        value={addr.street ? addr.street : ""}
                                                        onChange={onAddressChange}
                                                        disabled={isSaving}
                                                        data-index={i}
                                                    />
                                                </Col>
                                                <Col width={4}>
                                                    <FloatInput
                                                        type="text"
                                                        name="housenumber"
                                                        placeholder="Hausnr."
                                                        className="mb-2"
                                                        value={addr.housenumber ? addr.housenumber : ""}
                                                        onChange={onAddressChange}
                                                        disabled={isSaving}
                                                        data-index={i}
                                                    />
                                                </Col>
                                                <Col width={4} className="mb-2">
                                                    <FloatInput
                                                        type="text"
                                                        name="zip"
                                                        placeholder="PLZ"
                                                        value={addr.zip ? addr.zip : ""}
                                                        onChange={onAddressChange}
                                                        disabled={isSaving}
                                                        data-index={i}
                                                    />
                                                </Col>
                                                <Col width={8} className="mb-2">
                                                    <FloatInput
                                                        type="text"
                                                        name="city"
                                                        placeholder="Stadt"
                                                        value={addr.city ? addr.city : ""}
                                                        onChange={onAddressChange}
                                                        disabled={isSaving}
                                                        data-index={i}
                                                    />
                                                </Col>
                                                <Col width={12}>
                                                    <Button
                                                        type="button"
                                                        disabled={isSaving}
                                                        data-index={i}
                                                        onClick={onAddressRemove}
                                                        danger
                                                        outline
                                                        xs
                                                    >
                                                        Entfernen
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    ))
                                )}
                            </Card>
                        </Col>

                        <Col width={8}>
                            <Card className="mb-3">
                                <CardHeader>
                                    <Button
                                        type="button"
                                        className="float-right"
                                        disabled={isSaving}
                                        onClick={onAddPerson}
                                        xs
                                    >
                                        <Icon icon={faPlus} className="mr-1" />
                                        Hinzufügen
                                    </Button>
                                    Ansprechpartner
                                </CardHeader>
                                <Table className="valign-middle">
                                    <thead>
                                        <tr>
                                            <th>Vorname</th>
                                            <th>Nachname</th>
                                            <th>E-Mail</th>
                                            <th className="text-center" style={{ width: 58 }}>
                                                <Icon icon={faCheck} />
                                            </th>
                                            <th className="text-right" style={{ width: 100 }}>
                                                Optionen
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!data.persons || data.persons.length < 1 ? (
                                            <tr>
                                                <td colSpan={5}>
                                                    Es wurden noch keine Ansprechpartner hinzugefügt ...
                                                </td>
                                            </tr>
                                        ) : (
                                            data.persons.map((person, i) => (
                                                <React.Fragment key={i}>
                                                    <tr>
                                                        <td>
                                                            <Input
                                                                type="text"
                                                                name="firstname"
                                                                placeholder="Vorname"
                                                                value={person.firstname ? person.firstname : ""}
                                                                onChange={onPersonChange}
                                                                disabled={isSaving}
                                                                data-index={i}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="text"
                                                                name="lastname"
                                                                placeholder="Nachname"
                                                                value={person.lastname ? person.lastname : ""}
                                                                onChange={onPersonChange}
                                                                disabled={isSaving}
                                                                data-index={i}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="text"
                                                                name="email"
                                                                placeholder="E-Mail"
                                                                value={person.email ? person.email : ""}
                                                                onChange={onPersonChange}
                                                                disabled={isSaving}
                                                                data-index={i}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Switch
                                                                name="is_active"
                                                                checked={person.is_active ? person.is_active : ""}
                                                                onChange={(e) =>
                                                                    onPersonActiveChange(i, e.target.checked)
                                                                }
                                                                disabled={isSaving}
                                                            />
                                                        </td>
                                                        <td className="text-right">
                                                            <ButtonGroup>
                                                                <Button
                                                                    type="button"
                                                                    color={
                                                                        person.new_password &&
                                                                        person.new_password.trim() !== ""
                                                                            ? "dark"
                                                                            : "light"
                                                                    }
                                                                    disabled={isSaving}
                                                                    data-index={i}
                                                                    onClick={openPersonNewPassword}
                                                                    sm
                                                                >
                                                                    <Icon icon={faLock} />
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    data-index={i}
                                                                    onClick={onPersonRemove}
                                                                    disabled={isSaving}
                                                                    danger
                                                                    sm
                                                                >
                                                                    <Icon icon={faTrashAlt} />
                                                                </Button>
                                                            </ButtonGroup>
                                                        </td>
                                                    </tr>
                                                    {person.is_active && person.identifier === "new" ? (
                                                        !person.new_password || person.new_password.trim() === "" ? (
                                                            <tr>
                                                                <td
                                                                    colSpan={5}
                                                                    style={{ paddingTop: 0, borderTop: "none" }}
                                                                >
                                                                    <div
                                                                        className="text-danger"
                                                                        style={{ fontSize: 12 }}
                                                                    >
                                                                        Fehlendes Passwort ...
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ) : null
                                                    ) : null}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </Card>
                        </Col>

                        <Col width={12} className="text-right">
                            <Button disabled={isSaving}>{isSaving ? <LoadingSpinner /> : "Speichern"}</Button>
                        </Col>
                    </Row>
                </form>
            </Content>
        </Page>
    );
};
